// Copyright (c) 2026 Joshua S. Plaza. All rights reserved.

import { createTheme, alpha, responsiveFontSizes } from '@mui/material/styles';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * RNHS Portal - "ILLUMINATE v2" Premium Theme
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * THEME NAME: Illuminate v2
 *
 * BRAND PERSONALITY:
 * - Trustworthy
 * - Modern
 * - Calm
 * - Academic Excellence
 *
 * DESIGN PRINCIPLES:
 * ✔ Clarity over decoration
 * ✔ Space = luxury
 * ✔ Subtle motion, never flashy
 * ✔ Accessibility-first
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * COLOR PALETTE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * PRIMARY - Navy Blue
 * Hex: #1A365D
 * Usage: CTAs, links, icons, primary buttons, active states
 *
 * SECONDARY - Muted Gold
 * Hex: #C5A55A
 * Usage: Highlights, badges, achievements, premium indicators
 *
 * BACKGROUNDS:
 * - White: #FFFFFF (Main background, cards)
 * - Light Gray: #F8F9FA (Section backgrounds)
 *
 * TEXT COLORS:
 * - Dark Text: #121212 (Headings)
 * - Body Text: #4A4A4A (Paragraph text)
 *
 * BORDERS:
 * - Border Gray: #D1D5DB (Cards, inputs)
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * TYPOGRAPHY
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * FONT FAMILIES:
 * - Primary: Inter
 * - Fallback: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif
 *
 * TEXT STYLES:
 * - H1: 40px, 700 weight, line-height 1.2
 * - H2: 32px, 600 weight, line-height 1.25
 * - H3: 24px, 500 weight, line-height 1.3
 * - Body Large: 18px, 400 weight, line-height 1.6
 * - Body Small: 16px, 400 weight, line-height 1.5
 * - Button: 16px, 600 weight, line-height 1
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * ACCESSIBILITY NOTES
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * - All color combinations meet WCAG AA+ (4.5:1 for normal text, 3:1 for large)
 * - Focus states use visible ring (2px Primary Blue)
 * - Interactive elements have minimum 44px touch targets
 * - Minimum font size: 16px
 * - Keyboard navigation supported
 * - Alt text on images
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

// ═══════════════════════════════════════════════════════════════════════════════
// ILLUMINATE v2 COLOR DEFINITIONS
// ═══════════════════════════════════════════════════════════════════════════════

const illuminateColors = {
  // Primary - Navy Blue (Trustworthy, Professional)
  primaryBlue: {
    main: '#1A365D',
    light: '#2D4A7A', // +8% brightness for hover
    dark: '#0F2440',
    contrastText: '#FFFFFF',
  },

  // Secondary - Muted Gold Accent (Achievement, Excellence)
  goldAccent: {
    main: '#C5A55A',
    light: '#D4BC7A',
    dark: '#A8883E',
    contrastText: '#FFFFFF',
  },

  // Light Mode Backgrounds
  backgrounds: {
    white: '#FFFFFF',        // Main background, cards
    lightGray: '#F8F9FA',    // Section backgrounds
    hover: '#F1F3F5',        // Hover states
  },

  // Text Colors
  text: {
    dark: '#121212',         // Headings (high emphasis)
    body: '#4A4A4A',         // Paragraph text (medium emphasis)
    muted: '#6B7280',        // Captions, placeholders (low emphasis)
    disabled: '#9CA3AF',     // Disabled state
  },

  // Borders
  borders: {
    gray: '#D1D5DB',         // Cards, inputs
    light: '#E5E7EB',        // Subtle borders
    focus: '#0056B3',        // Focus state
  },

  // Status Colors (WCAG AA Compliant)
  status: {
    success: {
      main: '#059669',
      light: '#10B981',
      dark: '#047857',
      bg: 'rgba(5, 150, 105, 0.1)',
    },
    warning: {
      main: '#D97706',
      light: '#F59E0B',
      dark: '#B45309',
      bg: 'rgba(217, 119, 6, 0.1)',
    },
    error: {
      main: '#DC2626',
      light: '#EF4444',
      dark: '#B91C1C',
      bg: 'rgba(220, 38, 38, 0.1)',
    },
    info: {
      main: '#0284C7',
      light: '#0EA5E9',
      dark: '#0369A1',
      bg: 'rgba(2, 132, 199, 0.1)',
    },
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// LIGHT THEME (PRIMARY) - ILLUMINATE v2
// ═══════════════════════════════════════════════════════════════════════════════

const lightTheme = responsiveFontSizes(createTheme({
  palette: {
    mode: 'light',

    // Primary - Primary Blue
    primary: {
      main: illuminateColors.primaryBlue.main,
      light: illuminateColors.primaryBlue.light,
      dark: illuminateColors.primaryBlue.dark,
      contrastText: illuminateColors.primaryBlue.contrastText,
    },

    // Secondary - Gold Accent
    secondary: {
      main: illuminateColors.goldAccent.main,
      light: illuminateColors.goldAccent.light,
      dark: illuminateColors.goldAccent.dark,
      contrastText: illuminateColors.goldAccent.contrastText,
    },

    // Status Colors
    success: {
      main: illuminateColors.status.success.main,
      light: illuminateColors.status.success.light,
      dark: illuminateColors.status.success.dark,
    },
    warning: {
      main: illuminateColors.status.warning.main,
      light: illuminateColors.status.warning.light,
      dark: illuminateColors.status.warning.dark,
    },
    error: {
      main: illuminateColors.status.error.main,
      light: illuminateColors.status.error.light,
      dark: illuminateColors.status.error.dark,
    },
    info: {
      main: illuminateColors.status.info.main,
      light: illuminateColors.status.info.light,
      dark: illuminateColors.status.info.dark,
    },

    // Backgrounds
    background: {
      default: illuminateColors.backgrounds.lightGray,
      paper: illuminateColors.backgrounds.white,
    },

    // Text
    text: {
      primary: illuminateColors.text.dark,
      secondary: illuminateColors.text.body,
      disabled: illuminateColors.text.disabled,
    },

    // Dividers
    divider: illuminateColors.borders.gray,

    // Custom palette extensions
    gold: illuminateColors.goldAccent,

    // Action states
    action: {
      active: illuminateColors.text.dark,
      hover: alpha(illuminateColors.primaryBlue.main, 0.04),
      selected: alpha(illuminateColors.primaryBlue.main, 0.08),
      disabled: illuminateColors.text.disabled,
      disabledBackground: alpha(illuminateColors.borders.gray, 0.3),
      focus: alpha(illuminateColors.primaryBlue.main, 0.12),
    },
  },

  // Typography - Montserrat for headings, Source Serif Pro for body
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",

    h1: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '2.5rem', // 40px
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      color: illuminateColors.text.dark,
    },
    h2: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '2rem', // 32px
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
      color: illuminateColors.text.dark,
    },
    h3: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '1.5rem', // 24px
      fontWeight: 500,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      color: illuminateColors.text.dark,
    },
    h4: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '1.25rem', // 20px
      fontWeight: 500,
      lineHeight: 1.35,
      color: illuminateColors.text.dark,
    },
    h5: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '1.125rem', // 18px
      fontWeight: 500,
      lineHeight: 1.4,
      color: illuminateColors.text.dark,
    },
    h6: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '1rem', // 16px
      fontWeight: 500,
      lineHeight: 1.5,
      color: illuminateColors.text.dark,
    },
    subtitle1: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '1.125rem', // 18px
      fontWeight: 500,
      lineHeight: 1.5,
      color: illuminateColors.text.body,
    },
    subtitle2: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '1rem', // 16px
      fontWeight: 500,
      lineHeight: 1.5,
      color: illuminateColors.text.body,
    },
    body1: {
      fontSize: '1.125rem', // 18px - Body Large
      fontWeight: 400,
      lineHeight: 1.6,
      color: illuminateColors.text.body,
    },
    body2: {
      fontSize: '1rem', // 16px - Body Small
      fontWeight: 400,
      lineHeight: 1.5,
      color: illuminateColors.text.body,
    },
    button: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '1rem', // 16px
      fontWeight: 600,
      lineHeight: 1,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    caption: {
      fontSize: '0.875rem', // 14px
      fontWeight: 400,
      lineHeight: 1.5,
      color: illuminateColors.text.muted,
    },
    overline: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '0.75rem', // 12px
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: illuminateColors.text.muted,
    },
  },

  // Shape
  shape: {
    borderRadius: 8,
  },

  // Shadows (Light, clean, minimal)
  shadows: [
    'none',
    '0 1px 2px rgba(0, 0, 0, 0.04)',
    '0 2px 4px rgba(0, 0, 0, 0.04)',
    '0 4px 8px rgba(0, 0, 0, 0.04)',
    '0 6px 12px rgba(0, 0, 0, 0.05)',
    '0 6px 24px rgba(0, 0, 0, 0.06)', // Card shadow as specified
    '0 8px 16px rgba(0, 0, 0, 0.06)',
    '0 12px 24px rgba(0, 0, 0, 0.06)',
    '0 16px 32px rgba(0, 0, 0, 0.08)',
    '0 20px 40px rgba(0, 0, 0, 0.08)',
    '0 24px 48px rgba(0, 0, 0, 0.08)',
    '0 28px 56px rgba(0, 0, 0, 0.08)',
    '0 32px 64px rgba(0, 0, 0, 0.08)',
    '0 36px 72px rgba(0, 0, 0, 0.08)',
    '0 40px 80px rgba(0, 0, 0, 0.08)',
    '0 44px 88px rgba(0, 0, 0, 0.08)',
    '0 48px 96px rgba(0, 0, 0, 0.08)',
    '0 52px 104px rgba(0, 0, 0, 0.08)',
    '0 56px 112px rgba(0, 0, 0, 0.08)',
    '0 60px 120px rgba(0, 0, 0, 0.08)',
    '0 64px 128px rgba(0, 0, 0, 0.08)',
    '0 68px 136px rgba(0, 0, 0, 0.08)',
    '0 72px 144px rgba(0, 0, 0, 0.08)',
    '0 76px 152px rgba(0, 0, 0, 0.08)',
    '0 80px 160px rgba(0, 0, 0, 0.08)',
  ],

  // Transitions
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPONENT OVERRIDES
  // ═══════════════════════════════════════════════════════════════════════════

  components: {
    // Base CSS
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: illuminateColors.backgrounds.lightGray,
          color: illuminateColors.text.body,
          scrollbarColor: `${illuminateColors.borders.gray} ${illuminateColors.backgrounds.lightGray}`,
          '&::-webkit-scrollbar': {
            width: '10px',
            height: '10px',
          },
          '&::-webkit-scrollbar-track': {
            background: illuminateColors.backgrounds.lightGray,
          },
          '&::-webkit-scrollbar-thumb': {
            background: illuminateColors.borders.gray,
            borderRadius: '5px',
            border: `2px solid ${illuminateColors.backgrounds.lightGray}`,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: illuminateColors.text.muted,
          },
        },
        // Focus visible for accessibility
        '*:focus-visible': {
          outline: `2px solid ${illuminateColors.primaryBlue.main}`,
          outlineOffset: '2px',
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // APP BAR / HEADER (Height: 80px)
    // ═══════════════════════════════════════════════════════════════════════
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: illuminateColors.backgrounds.white,
          color: illuminateColors.text.dark,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          borderBottom: `1px solid ${illuminateColors.borders.light}`,
          backdropFilter: 'blur(8px)',
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '56px !important',
          '@media (min-width: 600px)': {
            minHeight: '80px !important',
          },
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SIDEBAR / DRAWER
    // ═══════════════════════════════════════════════════════════════════════
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: illuminateColors.backgrounds.white,
          borderRight: `1px solid ${illuminateColors.borders.light}`,
          backgroundImage: 'none',
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BUTTONS
    // ═══════════════════════════════════════════════════════════════════════
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '12px 24px',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontWeight: 600,
          fontSize: '1rem',
          lineHeight: 1,
          transition: 'all 0.2s ease-in-out',
          '&:focus-visible': {
            outline: `2px solid ${illuminateColors.primaryBlue.main}`,
            outlineOffset: '2px',
          },
        },
        // Smaller button variant
        sizeSmall: {
          padding: '8px 16px',
          fontSize: '0.875rem',
        },
        sizeMedium: {
          padding: '10px 20px',
        },
        sizeLarge: {
          padding: '12px 28px',
        },
        // Primary Contained Button
        containedPrimary: {
          backgroundColor: illuminateColors.primaryBlue.main,
          color: illuminateColors.primaryBlue.contrastText,
          boxShadow: '0 2px 8px rgba(26, 54, 93, 0.25)',
          '&:hover': {
            backgroundColor: illuminateColors.primaryBlue.light,
            boxShadow: '0 4px 16px rgba(26, 54, 93, 0.3)',
          },
          '&:active': {
            transform: 'scale(1)',
          },
        },
        // Secondary Contained Button (Gold)
        containedSecondary: {
          backgroundColor: illuminateColors.goldAccent.main,
          color: illuminateColors.goldAccent.contrastText,
          boxShadow: '0 2px 8px rgba(197, 165, 90, 0.35)',
          '&:hover': {
            backgroundColor: illuminateColors.goldAccent.light,
            boxShadow: '0 4px 16px rgba(197, 165, 90, 0.4)',
          },
        },
        // Outlined Primary Button (Secondary style from spec)
        outlinedPrimary: {
          borderColor: illuminateColors.primaryBlue.main,
          borderWidth: '2px',
          color: illuminateColors.primaryBlue.main,
          backgroundColor: 'transparent',
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: illuminateColors.primaryBlue.main,
            color: illuminateColors.primaryBlue.contrastText,
          },
        },
        // Text Button
        text: {
          '&:hover': {
            backgroundColor: alpha(illuminateColors.primaryBlue.main, 0.04),
          },
        },
        textPrimary: {
          color: illuminateColors.primaryBlue.main,
          '&:hover': {
            backgroundColor: alpha(illuminateColors.primaryBlue.main, 0.04),
          },
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // CARDS
    // ═══════════════════════════════════════════════════════════════════════
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: illuminateColors.backgrounds.white,
          borderRadius: 8,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '24px',
          borderBottom: `1px solid ${illuminateColors.borders.light}`,
        },
        title: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontSize: '1.25rem',
          fontWeight: 600,
          color: illuminateColors.text.dark,
        },
        subheader: {
          color: illuminateColors.text.body,
          fontSize: '0.875rem',
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '&:last-child': {
            paddingBottom: '24px',
          },
        },
      },
    },

    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          borderTop: `1px solid ${illuminateColors.borders.light}`,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // PAPER
    // ═══════════════════════════════════════════════════════════════════════
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: illuminateColors.backgrounds.white,
        },
        outlined: {
          borderColor: illuminateColors.borders.gray,
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        },
        elevation2: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        },
        elevation3: {
          boxShadow: '0 6px 24px rgba(0, 0, 0, 0.06)',
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // TABLES
    // ═══════════════════════════════════════════════════════════════════════
    MuiTableContainer: {
      styleOverrides: {
        root: {
          overflowX: 'auto',
          backgroundColor: illuminateColors.backgrounds.white,
          borderRadius: 8,
          boxShadow: '0 6px 24px rgba(0, 0, 0, 0.06)',
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: alpha(illuminateColors.primaryBlue.main, 0.08),
            color: illuminateColors.text.dark,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            fontWeight: 600,
            fontSize: '0.875rem',
            letterSpacing: '0.02em',
            borderBottom: 'none',
            padding: '16px',
            '&:first-of-type': {
              borderTopLeftRadius: 8,
            },
            '&:last-of-type': {
              borderTopRightRadius: 8,
            },
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(even)': {
            backgroundColor: illuminateColors.backgrounds.lightGray,
          },
          '&:hover': {
            backgroundColor: illuminateColors.backgrounds.hover,
          },
          '&.Mui-selected': {
            backgroundColor: alpha(illuminateColors.primaryBlue.main, 0.08),
            '&:hover': {
              backgroundColor: alpha(illuminateColors.primaryBlue.main, 0.12),
            },
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${illuminateColors.borders.light}`,
          padding: '16px',
          color: illuminateColors.text.body,
          fontSize: '1rem',
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // FORMS (Input height: 48px)
    // ═══════════════════════════════════════════════════════════════════════
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: illuminateColors.backgrounds.white,
            borderRadius: 8,
            transition: 'all 0.2s ease-in-out',
            '& fieldset': {
              borderColor: illuminateColors.borders.gray,
              borderWidth: '1px',
            },
            '&:hover fieldset': {
              borderColor: illuminateColors.primaryBlue.light,
            },
            '&.Mui-focused fieldset': {
              borderColor: illuminateColors.primaryBlue.main,
              borderWidth: '2px',
            },
            '&.Mui-error fieldset': {
              borderColor: illuminateColors.status.error.main,
            },
          },
          '& .MuiOutlinedInput-input': {
            padding: '14px 16px', // Results in ~48px height
          },
          '& .MuiInputLabel-root': {
            color: illuminateColors.text.muted,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            '&.Mui-focused': {
              color: illuminateColors.primaryBlue.main,
            },
            '&.Mui-error': {
              color: illuminateColors.status.error.main,
            },
          },
          '& .MuiInputBase-input': {
            color: illuminateColors.text.dark,
            fontSize: '1rem',
            '&::placeholder': {
              color: illuminateColors.text.muted,
              opacity: 1,
            },
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: illuminateColors.borders.gray,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: illuminateColors.primaryBlue.light,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: illuminateColors.primaryBlue.main,
          },
        },
        input: {
          padding: '14px 16px',
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        icon: {
          color: illuminateColors.text.muted,
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          color: illuminateColors.text.dark,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: illuminateColors.text.muted,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontWeight: 500,
          '&.Mui-focused': {
            color: illuminateColors.primaryBlue.main,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontWeight: 500,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // NAVIGATION
    // ═══════════════════════════════════════════════════════════════════════
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: illuminateColors.backgrounds.hover,
          },
          '&.Mui-selected': {
            backgroundColor: alpha(illuminateColors.primaryBlue.main, 0.15),
            '&:hover': {
              backgroundColor: alpha(illuminateColors.primaryBlue.main, 0.2),
            },
            '& .MuiListItemIcon-root': {
              color: illuminateColors.primaryBlue.main,
            },
            '& .MuiListItemText-primary': {
              color: illuminateColors.primaryBlue.main,
              fontWeight: 600,
            },
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: illuminateColors.text.muted,
          minWidth: 40,
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: illuminateColors.text.body,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontSize: '0.9rem',
          fontWeight: 500,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // DIALOGS
    // ═══════════════════════════════════════════════════════════════════════
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: illuminateColors.backgrounds.white,
          borderRadius: 16,
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.15)',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontSize: '1.5rem',
          fontWeight: 600,
          color: illuminateColors.text.dark,
          padding: '24px',
          borderBottom: `1px solid ${illuminateColors.borders.light}`,
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          color: illuminateColors.text.body,
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          borderTop: `1px solid ${illuminateColors.borders.light}`,
          gap: 12,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // CHIPS / BADGES
    // ═══════════════════════════════════════════════════════════════════════
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontWeight: 500,
          fontSize: '0.8125rem',
        },
        colorPrimary: {
          backgroundColor: alpha(illuminateColors.primaryBlue.main, 0.1),
          color: illuminateColors.primaryBlue.main,
        },
        colorSecondary: {
          backgroundColor: alpha(illuminateColors.goldAccent.main, 0.2),
          color: illuminateColors.goldAccent.dark,
        },
        colorSuccess: {
          backgroundColor: illuminateColors.status.success.bg,
          color: illuminateColors.status.success.dark,
        },
        colorError: {
          backgroundColor: illuminateColors.status.error.bg,
          color: illuminateColors.status.error.dark,
        },
        colorWarning: {
          backgroundColor: illuminateColors.status.warning.bg,
          color: illuminateColors.status.warning.dark,
        },
        colorInfo: {
          backgroundColor: illuminateColors.status.info.bg,
          color: illuminateColors.status.info.dark,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // ALERTS
    // ═══════════════════════════════════════════════════════════════════════
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          border: '1px solid',
        },
        standardSuccess: {
          backgroundColor: illuminateColors.status.success.bg,
          color: illuminateColors.status.success.dark,
          borderColor: alpha(illuminateColors.status.success.main, 0.3),
          '& .MuiAlert-icon': {
            color: illuminateColors.status.success.main,
          },
        },
        standardError: {
          backgroundColor: illuminateColors.status.error.bg,
          color: illuminateColors.status.error.dark,
          borderColor: alpha(illuminateColors.status.error.main, 0.3),
          '& .MuiAlert-icon': {
            color: illuminateColors.status.error.main,
          },
        },
        standardWarning: {
          backgroundColor: illuminateColors.status.warning.bg,
          color: illuminateColors.status.warning.dark,
          borderColor: alpha(illuminateColors.status.warning.main, 0.3),
          '& .MuiAlert-icon': {
            color: illuminateColors.status.warning.main,
          },
        },
        standardInfo: {
          backgroundColor: illuminateColors.status.info.bg,
          color: illuminateColors.status.info.dark,
          borderColor: alpha(illuminateColors.status.info.main, 0.3),
          '& .MuiAlert-icon': {
            color: illuminateColors.status.info.main,
          },
        },
        filledSuccess: {
          backgroundColor: illuminateColors.status.success.main,
        },
        filledError: {
          backgroundColor: illuminateColors.status.error.main,
        },
        filledWarning: {
          backgroundColor: illuminateColors.status.warning.main,
          color: '#FFFFFF',
        },
        filledInfo: {
          backgroundColor: illuminateColors.status.info.main,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // TABS
    // ═══════════════════════════════════════════════════════════════════════
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${illuminateColors.borders.light}`,
        },
        indicator: {
          backgroundColor: illuminateColors.primaryBlue.main,
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontWeight: 500,
          fontSize: '0.9rem',
          color: illuminateColors.text.muted,
          minHeight: 48,
          padding: '12px 20px',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            color: illuminateColors.text.dark,
            backgroundColor: illuminateColors.backgrounds.hover,
          },
          '&.Mui-selected': {
            color: illuminateColors.primaryBlue.main,
            fontWeight: 600,
          },
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // PROGRESS
    // ═══════════════════════════════════════════════════════════════════════
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: illuminateColors.borders.light,
          height: 6,
        },
        barColorPrimary: {
          backgroundColor: illuminateColors.primaryBlue.main,
          borderRadius: 4,
        },
        barColorSecondary: {
          backgroundColor: illuminateColors.goldAccent.main,
          borderRadius: 4,
        },
      },
    },

    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: illuminateColors.primaryBlue.main,
        },
        colorSecondary: {
          color: illuminateColors.goldAccent.main,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // AVATAR
    // ═══════════════════════════════════════════════════════════════════════
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: illuminateColors.primaryBlue.main,
          color: '#FFFFFF',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontWeight: 600,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // TOOLTIPS
    // ═══════════════════════════════════════════════════════════════════════
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: illuminateColors.text.dark,
          color: '#FFFFFF',
          borderRadius: 6,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontSize: '0.875rem',
          fontWeight: 400,
          padding: '8px 12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
        arrow: {
          color: illuminateColors.text.dark,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // ICONS
    // ═══════════════════════════════════════════════════════════════════════
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: illuminateColors.text.muted,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: illuminateColors.backgrounds.hover,
            color: illuminateColors.text.dark,
          },
        },
        colorPrimary: {
          color: illuminateColors.primaryBlue.main,
          '&:hover': {
            backgroundColor: alpha(illuminateColors.primaryBlue.main, 0.1),
          },
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // DIVIDERS
    // ═══════════════════════════════════════════════════════════════════════
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: illuminateColors.borders.light,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // MENUS
    // ═══════════════════════════════════════════════════════════════════════
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: illuminateColors.backgrounds.white,
          border: `1px solid ${illuminateColors.borders.light}`,
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          fontSize: '0.9rem',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          transition: 'all 0.15s ease-in-out',
          '&:hover': {
            backgroundColor: illuminateColors.backgrounds.hover,
          },
          '&.Mui-selected': {
            backgroundColor: alpha(illuminateColors.primaryBlue.main, 0.1),
            '&:hover': {
              backgroundColor: alpha(illuminateColors.primaryBlue.main, 0.15),
            },
          },
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // TOGGLE BUTTONS
    // ═══════════════════════════════════════════════════════════════════════
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          borderColor: illuminateColors.borders.gray,
          color: illuminateColors.text.body,
          textTransform: 'none',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontWeight: 500,
          padding: '10px 16px',
          '&:hover': {
            backgroundColor: illuminateColors.backgrounds.hover,
          },
          '&.Mui-selected': {
            backgroundColor: illuminateColors.primaryBlue.main,
            color: '#FFFFFF',
            borderColor: illuminateColors.primaryBlue.main,
            '&:hover': {
              backgroundColor: illuminateColors.primaryBlue.light,
            },
          },
        },
      },
    },

    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: illuminateColors.backgrounds.white,
          borderRadius: 8,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SNACKBAR
    // ═══════════════════════════════════════════════════════════════════════
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiAlert-root': {
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // PAGINATION
    // ═══════════════════════════════════════════════════════════════════════
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            color: illuminateColors.text.body,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            borderColor: illuminateColors.borders.gray,
            '&:hover': {
              backgroundColor: illuminateColors.backgrounds.hover,
            },
            '&.Mui-selected': {
              backgroundColor: illuminateColors.primaryBlue.main,
              color: '#FFFFFF',
              borderColor: illuminateColors.primaryBlue.main,
              '&:hover': {
                backgroundColor: illuminateColors.primaryBlue.light,
              },
            },
          },
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // CHECKBOX & RADIO
    // ═══════════════════════════════════════════════════════════════════════
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: illuminateColors.borders.gray,
          '&.Mui-checked': {
            color: illuminateColors.primaryBlue.main,
          },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          color: illuminateColors.borders.gray,
          '&.Mui-checked': {
            color: illuminateColors.primaryBlue.main,
          },
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SWITCH
    // ═══════════════════════════════════════════════════════════════════════
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase': {
            '&.Mui-checked': {
              color: illuminateColors.primaryBlue.main,
              '& + .MuiSwitch-track': {
                backgroundColor: illuminateColors.primaryBlue.main,
                opacity: 0.5,
              },
            },
          },
          '& .MuiSwitch-track': {
            backgroundColor: illuminateColors.borders.gray,
          },
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SLIDER
    // ═══════════════════════════════════════════════════════════════════════
    MuiSlider: {
      styleOverrides: {
        root: {
          color: illuminateColors.primaryBlue.main,
          '& .MuiSlider-track': {
            backgroundColor: illuminateColors.primaryBlue.main,
          },
          '& .MuiSlider-rail': {
            backgroundColor: illuminateColors.borders.gray,
          },
          '& .MuiSlider-thumb': {
            backgroundColor: illuminateColors.primaryBlue.main,
            '&:hover, &.Mui-focusVisible': {
              boxShadow: `0 0 0 8px ${alpha(illuminateColors.primaryBlue.main, 0.16)}`,
            },
          },
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BREADCRUMBS
    // ═══════════════════════════════════════════════════════════════════════
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          '& .MuiLink-root': {
            color: illuminateColors.text.muted,
            textDecoration: 'none',
            '&:hover': {
              color: illuminateColors.primaryBlue.main,
              textDecoration: 'underline',
            },
          },
          '& .MuiBreadcrumbs-separator': {
            color: illuminateColors.text.muted,
          },
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // ACCORDION
    // ═══════════════════════════════════════════════════════════════════════
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: illuminateColors.backgrounds.white,
          borderRadius: '8px !important',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
          marginBottom: 8,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: '0 0 8px 0',
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover': {
            backgroundColor: illuminateColors.backgrounds.hover,
          },
          '&.Mui-expanded': {
            borderBottom: `1px solid ${illuminateColors.borders.light}`,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        },
        expandIconWrapper: {
          color: illuminateColors.text.muted,
        },
      },
    },

    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '24px',
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // STEPPER
    // ═══════════════════════════════════════════════════════════════════════
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          color: illuminateColors.text.muted,
          '&.Mui-active': {
            color: illuminateColors.text.dark,
            fontWeight: 600,
          },
          '&.Mui-completed': {
            color: illuminateColors.status.success.main,
          },
        },
      },
    },

    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: illuminateColors.borders.gray,
          '&.Mui-active': {
            color: illuminateColors.primaryBlue.main,
          },
          '&.Mui-completed': {
            color: illuminateColors.status.success.main,
          },
        },
      },
    },

    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: illuminateColors.borders.light,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // DATA GRID
    // ═══════════════════════════════════════════════════════════════════════
    MuiDataGrid: {
      styleOverrides: {
        root: {
          overflowX: 'auto',
          border: 'none',
          borderRadius: 8,
          backgroundColor: illuminateColors.backgrounds.white,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: alpha(illuminateColors.primaryBlue.main, 0.08),
            color: illuminateColors.text.dark,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          },
          '& .MuiDataGrid-columnHeader': {
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            fontWeight: 600,
          },
          '& .MuiDataGrid-cell': {
            borderColor: illuminateColors.borders.light,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: illuminateColors.backgrounds.hover,
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: alpha(illuminateColors.primaryBlue.main, 0.08),
            '&:hover': {
              backgroundColor: alpha(illuminateColors.primaryBlue.main, 0.12),
            },
          },
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // LINK
    // ═══════════════════════════════════════════════════════════════════════
    MuiLink: {
      styleOverrides: {
        root: {
          color: illuminateColors.primaryBlue.main,
          textDecoration: 'none',
          transition: 'color 0.2s ease-in-out',
          '&:hover': {
            color: illuminateColors.primaryBlue.light,
            textDecoration: 'underline',
          },
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SKELETON
    // ═══════════════════════════════════════════════════════════════════════
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: illuminateColors.borders.light,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BADGE
    // ═══════════════════════════════════════════════════════════════════════
    MuiBadge: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: illuminateColors.primaryBlue.main,
        },
        colorSecondary: {
          backgroundColor: illuminateColors.goldAccent.main,
          color: illuminateColors.goldAccent.contrastText,
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // FAB (Floating Action Button)
    // ═══════════════════════════════════════════════════════════════════════
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: '0 6px 24px rgba(0, 0, 0, 0.15)',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          },
        },
        primary: {
          backgroundColor: illuminateColors.primaryBlue.main,
          '&:hover': {
            backgroundColor: illuminateColors.primaryBlue.light,
          },
        },
        secondary: {
          backgroundColor: illuminateColors.goldAccent.main,
          color: illuminateColors.goldAccent.contrastText,
          '&:hover': {
            backgroundColor: illuminateColors.goldAccent.light,
          },
        },
      },
    },

    // ═══════════════════════════════════════════════════════════════════════
    // RATING
    // ═══════════════════════════════════════════════════════════════════════
    MuiRating: {
      styleOverrides: {
        iconFilled: {
          color: illuminateColors.goldAccent.main,
        },
        iconHover: {
          color: illuminateColors.goldAccent.light,
        },
      },
    },
  },
}));

// ═══════════════════════════════════════════════════════════════════════════════
// DARK THEME (SECONDARY) - ILLUMINATE v2
// ═══════════════════════════════════════════════════════════════════════════════

const darkTheme = responsiveFontSizes(createTheme({
  palette: {
    mode: 'dark',

    // Primary - Navy Blue (Dark mode)
    primary: {
      main: '#4A90D9', // Lighter navy for dark mode visibility
      light: '#6BA3E0',
      dark: '#2D6BB5',
      contrastText: '#FFFFFF',
    },

    // Secondary - Gold Accent
    secondary: {
      main: illuminateColors.goldAccent.main,
      light: illuminateColors.goldAccent.light,
      dark: illuminateColors.goldAccent.dark,
      contrastText: illuminateColors.goldAccent.contrastText,
    },

    // Status Colors
    success: {
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#D97706',
    },
    error: {
      main: '#EF4444',
      light: '#F87171',
      dark: '#DC2626',
    },
    info: {
      main: '#0EA5E9',
      light: '#38BDF8',
      dark: '#0284C7',
    },

    // Backgrounds
    background: {
      default: '#0F172A',
      paper: '#1E293B',
    },

    // Text
    text: {
      primary: '#F1F5F9',
      secondary: '#94A3B8',
      disabled: '#475569',
    },

    // Dividers
    divider: '#334155',

    // Custom palette extensions
    gold: illuminateColors.goldAccent,

    // Action states
    action: {
      active: '#F1F5F9',
      hover: 'rgba(74, 144, 217, 0.08)',
      selected: 'rgba(74, 144, 217, 0.16)',
      disabled: '#475569',
      disabledBackground: 'rgba(51, 65, 85, 0.5)',
      focus: 'rgba(74, 144, 217, 0.24)',
    },
  },

  // Typography - Same fonts, different colors
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",

    h1: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
      color: '#F1F5F9',
    },
    h2: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.015em',
      color: '#F1F5F9',
    },
    h3: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
      color: '#F1F5F9',
    },
    h4: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.35,
      color: '#F1F5F9',
    },
    h5: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.4,
      color: '#F1F5F9',
    },
    h6: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#F1F5F9',
    },
    subtitle1: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#94A3B8',
    },
    subtitle2: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
      color: '#94A3B8',
    },
    body1: {
      fontSize: '1.125rem',
      fontWeight: 400,
      lineHeight: 1.6,
      color: '#94A3B8',
    },
    body2: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#94A3B8',
    },
    button: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    caption: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#64748B',
    },
    overline: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: '#64748B',
    },
  },

  // Shape
  shape: {
    borderRadius: 8,
  },

  // Shadows (Dark mode optimized)
  shadows: [
    'none',
    '0 1px 2px rgba(0, 0, 0, 0.3)',
    '0 2px 4px rgba(0, 0, 0, 0.3)',
    '0 4px 8px rgba(0, 0, 0, 0.3)',
    '0 6px 12px rgba(0, 0, 0, 0.3)',
    '0 6px 24px rgba(0, 0, 0, 0.35)',
    '0 8px 16px rgba(0, 0, 0, 0.35)',
    '0 12px 24px rgba(0, 0, 0, 0.35)',
    '0 16px 32px rgba(0, 0, 0, 0.4)',
    '0 20px 40px rgba(0, 0, 0, 0.4)',
    '0 24px 48px rgba(0, 0, 0, 0.4)',
    '0 28px 56px rgba(0, 0, 0, 0.4)',
    '0 32px 64px rgba(0, 0, 0, 0.4)',
    '0 36px 72px rgba(0, 0, 0, 0.4)',
    '0 40px 80px rgba(0, 0, 0, 0.4)',
    '0 44px 88px rgba(0, 0, 0, 0.4)',
    '0 48px 96px rgba(0, 0, 0, 0.4)',
    '0 52px 104px rgba(0, 0, 0, 0.4)',
    '0 56px 112px rgba(0, 0, 0, 0.4)',
    '0 60px 120px rgba(0, 0, 0, 0.4)',
    '0 64px 128px rgba(0, 0, 0, 0.4)',
    '0 68px 136px rgba(0, 0, 0, 0.4)',
    '0 72px 144px rgba(0, 0, 0, 0.4)',
    '0 76px 152px rgba(0, 0, 0, 0.4)',
    '0 80px 160px rgba(0, 0, 0, 0.4)',
  ],

  // ═══════════════════════════════════════════════════════════════════════════
  // DARK MODE COMPONENT OVERRIDES
  // ═══════════════════════════════════════════════════════════════════════════

  components: {
    // Base CSS
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0F172A',
          color: '#F1F5F9',
          scrollbarColor: '#475569 #1E293B',
          '&::-webkit-scrollbar': {
            width: '10px',
            height: '10px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#1E293B',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#475569',
            borderRadius: '5px',
            border: '2px solid #1E293B',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#64748B',
          },
        },
        '*:focus-visible': {
          outline: '2px solid #4A90D9',
          outlineOffset: '2px',
        },
      },
    },

    // App Bar
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E293B',
          color: '#F1F5F9',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
          borderBottom: '1px solid #334155',
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '56px !important',
          '@media (min-width: 600px)': {
            minHeight: '80px !important',
          },
        },
      },
    },

    // Drawer
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1E293B',
          borderRight: '1px solid #334155',
          backgroundImage: 'none',
        },
      },
    },

    // Buttons
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '12px 24px',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontWeight: 600,
          fontSize: '1rem',
          lineHeight: 1,
          transition: 'all 0.2s ease-in-out',
        },
        sizeSmall: {
          padding: '8px 16px',
          fontSize: '0.875rem',
        },
        sizeMedium: {
          padding: '10px 20px',
        },
        sizeLarge: {
          padding: '12px 28px',
        },
        containedPrimary: {
          backgroundColor: '#4A90D9',
          '&:hover': {
            backgroundColor: '#6BA3E0',
          },
        },
        containedSecondary: {
          backgroundColor: illuminateColors.goldAccent.main,
          color: illuminateColors.goldAccent.contrastText,
          '&:hover': {
            backgroundColor: illuminateColors.goldAccent.light,
          },
        },
        outlinedPrimary: {
          borderColor: '#4A90D9',
          borderWidth: '2px',
          color: '#4A90D9',
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: '#4A90D9',
            color: '#FFFFFF',
          },
        },
      },
    },

    // Cards
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E293B',
          borderRadius: 8,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
          },
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '24px',
          borderBottom: '1px solid #334155',
        },
        title: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          fontSize: '1.25rem',
          fontWeight: 600,
          color: '#F1F5F9',
        },
        subheader: {
          color: '#94A3B8',
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
          '&:last-child': {
            paddingBottom: '24px',
          },
        },
      },
    },

    // Paper
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1E293B',
        },
        outlined: {
          borderColor: '#334155',
        },
      },
    },

    // Tables
    MuiTableContainer: {
      styleOverrides: {
        root: {
          overflowX: 'auto',
          backgroundColor: '#1E293B',
          borderRadius: 8,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: alpha('#4A90D9', 0.15),
            color: '#F1F5F9',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            fontWeight: 600,
            borderBottom: 'none',
            '&:first-of-type': {
              borderTopLeftRadius: 8,
            },
            '&:last-of-type': {
              borderTopRightRadius: 8,
            },
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(even)': {
            backgroundColor: 'rgba(51, 65, 85, 0.3)',
          },
          '&:hover': {
            backgroundColor: 'rgba(51, 65, 85, 0.5)',
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #334155',
          color: '#94A3B8',
        },
      },
    },

    // Data Grid
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: alpha('#4A90D9', 0.15),
            color: '#F1F5F9',
          },
        },
      },
    },

    // Forms
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#0F172A',
            '& fieldset': {
              borderColor: '#334155',
            },
            '&:hover fieldset': {
              borderColor: '#4A90D9',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#4A90D9',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#64748B',
            '&.Mui-focused': {
              color: '#4A90D9',
            },
          },
          '& .MuiInputBase-input': {
            color: '#F1F5F9',
          },
        },
      },
    },

    // Navigation
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: 'rgba(51, 65, 85, 0.5)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(74, 144, 217, 0.25)',
            '& .MuiListItemIcon-root': {
              color: '#4A90D9',
            },
            '& .MuiListItemText-primary': {
              color: '#4A90D9',
              fontWeight: 600,
            },
          },
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#64748B',
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: '#94A3B8',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        },
      },
    },

    // Dialogs
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1E293B',
          borderRadius: 16,
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          color: '#F1F5F9',
          borderBottom: '1px solid #334155',
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          color: '#94A3B8',
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: '1px solid #334155',
        },
      },
    },

    // Chips
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: 'rgba(74, 144, 217, 0.2)',
          color: '#6BA3E0',
        },
        colorSecondary: {
          backgroundColor: 'rgba(197, 165, 90, 0.2)',
          color: '#D4BC7A',
        },
      },
    },

    // Tabs
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #334155',
        },
        indicator: {
          backgroundColor: '#4A90D9',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          color: '#64748B',
          '&:hover': {
            color: '#F1F5F9',
            backgroundColor: 'rgba(51, 65, 85, 0.3)',
          },
          '&.Mui-selected': {
            color: '#4A90D9',
          },
        },
      },
    },

    // Progress
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: '#334155',
        },
        barColorPrimary: {
          backgroundColor: '#4A90D9',
        },
      },
    },

    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: '#4A90D9',
        },
      },
    },

    // Dividers
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#334155',
        },
      },
    },

    // Menus
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1E293B',
          border: '1px solid #334155',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(51, 65, 85, 0.5)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(74, 144, 217, 0.2)',
          },
        },
      },
    },

    // Toggle Buttons
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderColor: '#334155',
          color: '#94A3B8',
          '&:hover': {
            backgroundColor: 'rgba(51, 65, 85, 0.3)',
          },
          '&.Mui-selected': {
            backgroundColor: '#4A90D9',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#6BA3E0',
            },
          },
        },
      },
    },

    // Checkbox & Radio
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#475569',
          '&.Mui-checked': {
            color: '#4A90D9',
          },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#475569',
          '&.Mui-checked': {
            color: '#4A90D9',
          },
        },
      },
    },

    // Switch
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#4A90D9',
            '& + .MuiSwitch-track': {
              backgroundColor: '#4A90D9',
            },
          },
          '& .MuiSwitch-track': {
            backgroundColor: '#475569',
          },
        },
      },
    },

    // Tooltips
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#334155',
          color: '#F1F5F9',
        },
        arrow: {
          color: '#334155',
        },
      },
    },

    // Pagination
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            color: '#94A3B8',
            borderColor: '#334155',
            '&:hover': {
              backgroundColor: 'rgba(51, 65, 85, 0.3)',
            },
            '&.Mui-selected': {
              backgroundColor: '#4A90D9',
              color: '#FFFFFF',
            },
          },
        },
      },
    },

    // Accordion
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: '#1E293B',
          '&:before': {
            display: 'none',
          },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(51, 65, 85, 0.3)',
          },
          '&.Mui-expanded': {
            borderBottom: '1px solid #334155',
          },
        },
        expandIconWrapper: {
          color: '#64748B',
        },
      },
    },

    // Stepper
    MuiStepLabel: {
      styleOverrides: {
        label: {
          color: '#64748B',
          '&.Mui-active': {
            color: '#F1F5F9',
          },
          '&.Mui-completed': {
            color: '#10B981',
          },
        },
      },
    },

    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: '#475569',
          '&.Mui-active': {
            color: '#4A90D9',
          },
          '&.Mui-completed': {
            color: '#10B981',
          },
        },
      },
    },

    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: '#334155',
        },
      },
    },

    // Avatar
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: '#4A90D9',
          color: '#FFFFFF',
        },
      },
    },

    // Icons
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#64748B',
          '&:hover': {
            backgroundColor: 'rgba(51, 65, 85, 0.3)',
            color: '#F1F5F9',
          },
        },
      },
    },

    // Rating
    MuiRating: {
      styleOverrides: {
        iconFilled: {
          color: illuminateColors.goldAccent.main,
        },
        iconHover: {
          color: illuminateColors.goldAccent.light,
        },
      },
    },
  },
}));

// ═══════════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════════

// Export the light theme as default (light-mode-first approach for Illuminate v2)
export default lightTheme;

// Export both themes for dynamic switching
export { darkTheme, lightTheme, illuminateColors };

// For backwards compatibility with existing code that may import akademyaColors
export { illuminateColors as akademyaColors };
